/**
 * @fileoverview WEB-851 — the ticketing module's price story.
 *
 * The Event v2 page already prints one: `decisionPanel.cta.note`, assembled
 * server-side by `buildDecisionPanel` and rendered beneath the CTA at every
 * width ("$20.38–$23.69 · VIA EVENTBRITE"). WEB-851 asks the ticketing module
 * to print the same line with the provider name as a link to the ticket page.
 *
 * ## Why this reads the server's string instead of rebuilding it
 *
 * `buildDecisionPanel` is the declared single source of truth for the price
 * story, and the rules it encodes are not cosmetic: an explicit `isFree` beats
 * a price band, `priceKnown === false` yields no note at all rather than "$0",
 * whole dollars drop their cents and fractional bands keep them, and a fee
 * disclosure rides the same string. Re-deriving any of that from `priceMin` /
 * `priceMax` / `isFree` on the client would be a second implementation of the
 * D6 price-story rule, free to disagree with the panel two inches above it on
 * the same page. So this module formats nothing — it only decides where the
 * provider attribution the server already wrote begins, so that half can be
 * wrapped in an anchor.
 *
 * ## Why the split is anchored on the source name, not on a separator
 *
 * The note is `parts.join(' · ')` with the attribution last, so splitting on
 * the final `' · '` would work today and break the moment a part is added
 * after it. Instead the expected suffix is rebuilt from the event's own
 * `source.displayName` exactly as the server builds it (`VIA ${NAME}`, upper
 * case) and matched against the end of the note. A mismatch — a source with no
 * display name, a note that never carried attribution, a future server
 * wording — degrades to the whole note as plain text. The line still prints;
 * only the link is lost, which is the correct failure direction for copy that
 * must stay true.
 */

import { isUsableHttpUrl } from '@/lib/ticketing';
import type { EventDetailV2 } from '@/types/eventDetail';

/** The word the server prefixes provider attribution with. */
const VIA_PREFIX = 'VIA';

/** The ` · ` the server joins price-note parts with. */
const NOTE_SEPARATOR = ' · ';

export interface TicketingPriceNote {
  /**
   * The note up to and including the `VIA ` word — e.g. `'$20.38–$23.69 · VIA '`
   * — or the entire note when it carries no recognisable attribution. Rendered
   * as plain text.
   */
  lead: string;
  /**
   * The provider name to render as the link, upper-cased as the server wrote
   * it (`'EVENTBRITE'`). Null when the note carries no attribution this module
   * can identify, in which case `lead` is the whole note.
   */
  sourceLabel: string | null;
  /**
   * The same provider name in the source's own display casing
   * (`'Eventbrite'`). Carried separately so the link's accessible name can
   * read as a sentence — a screen reader announcing the visible caps run
   * alone gives a link named "EVENTBRITE" and no idea where it goes. Null
   * exactly when `sourceLabel` is.
   */
  sourceName: string | null;
  /**
   * The ticket destination — the SAME href the CTA button uses, so the two
   * cannot point at different pages. Null when the panel has no destination
   * (or one that is not http(s)); the source label then renders as plain text.
   */
  href: string | null;
}

function trimmedNonEmpty(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * The price story for the ticketing module, or `null` when the server told us
 * there is none.
 *
 * This is BOTH the module's render input and its `hasContent` gate
 * (`hasModuleContent('seatTiers')` calls it), the same arrangement
 * `resolveAboutColumnBody` has with the About column — one function decides
 * whether there is something to say and what it is, so the gate and the body
 * cannot disagree.
 *
 * Note that `decisionPanel.cta.note` is read raw rather than through
 * `resolveDecisionCtaNote`: that helper substitutes "No ticket link listed"
 * for a disabled CTA, which explains a missing link. This module has no button
 * to explain, and a price story is still true when nobody is selling.
 */
export function resolveTicketingPriceNote(event: EventDetailV2): TicketingPriceNote | null {
  const cta = event.decisionPanel?.cta;
  const note = trimmedNonEmpty(cta?.note);
  if (note === null) {
    return null;
  }

  const rawHref = typeof cta?.href === 'string' ? cta.href.trim() : '';
  const href = isUsableHttpUrl(rawHref) ? rawHref : null;

  const displayName = trimmedNonEmpty(event.source?.displayName);
  if (displayName === null) {
    return { lead: note, sourceLabel: null, sourceName: null, href };
  }

  const sourceLabel = displayName.toUpperCase();
  const attribution = `${VIA_PREFIX} ${sourceLabel}`;
  // Only a trailing attribution is split off, and only when it is the whole
  // final part — `endsWith` alone would also match a source name that happens
  // to end another word, which would slice the link mid-token.
  const isWholeNote = note === attribution;
  const isTrailingPart = note.endsWith(`${NOTE_SEPARATOR}${attribution}`);
  if (!isWholeNote && !isTrailingPart) {
    return { lead: note, sourceLabel: null, sourceName: null, href };
  }

  return {
    lead: note.slice(0, note.length - sourceLabel.length),
    sourceLabel,
    sourceName: displayName,
    href,
  };
}
